import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ComponentRef,
  effect,
  inject,
  input,
  inputBinding,
  output,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ApiClient } from '@core/services/api-client';
import { Button } from '@shared/components/button/button';
import { Select, SelectItem } from '@shared/components/select/select';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { User } from '@shared/models/user';

@Component({
  selector: 'app-user-modal',
  imports: [CommonModule, Button, Select, ReactiveFormsModule],
  templateUrl: './user-modal.html',
})
export class UserModal {
  private readonly apiClient = inject(ApiClient);
  private readonly fb = inject(FormBuilder);

  // Inputs and outputs
  showUserModal = input<boolean>(false);
  mode = input<'create' | 'edit'>('create');
  userData = input<User | null>(null);
  closed = output<void>();
  userSaved = output<void>();

  // Form and state
  userForm: FormGroup;
  isSubmitting = signal<boolean>(false);
  toastMessage = signal<string>('');

  // Toast management
  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });

  // Select component state
  selectedRole = signal<SelectItem>({ value: 'hr', label: 'HR' });
  readonly selectItems = signal<SelectItem[]>([
    { value: 'hr', label: 'HR' },
    { value: 'tl', label: 'TL' },
  ]);

  constructor() {
    this.userForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(1)]],
      lastName: ['', [Validators.required, Validators.minLength(1)]],
      email: ['', [Validators.required, Validators.email]],
      username: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[A-Za-z][A-Za-z0-9_]*$/),
          Validators.minLength(3),
          Validators.maxLength(30),
        ],
      ],
      password: [''],
      confirmPassword: [''],
    });

    // Effect to handle modal state changes
    effect(() => {
      const userData = this.userData();
      const mode = this.mode();
      const showModal = this.showUserModal();

      if (showModal) {
        this.resetFormState();

        if (mode === 'create') {
          this.setupCreateMode();
        } else if (mode === 'edit' && userData) {
          this.setupEditMode(userData);
        }
      }
    });
  }

  // Form control getters
  get firstName() {
    return this.userForm.get('firstName');
  }

  get lastName() {
    return this.userForm.get('lastName');
  }

  get email() {
    return this.userForm.get('email');
  }

  get username() {
    return this.userForm.get('username');
  }

  get password() {
    return this.userForm.get('password');
  }

  get confirmPassword() {
    return this.userForm.get('confirmPassword');
  }

  // Form setup methods
  private resetFormState(): void {
    this.userForm.markAsUntouched();
    this.userForm.markAsPristine();

    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();
        control.setErrors(null);
      }
    });
  }

  private setupCreateMode(): void {
    this.userForm.reset();
    this.userForm.patchValue({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
    });

    // Set password validators for create mode
    this.userForm
      .get('password')
      ?.setValidators([
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/),
      ]);
    this.userForm.get('confirmPassword')?.setValidators([Validators.required]);

    this.userForm.get('email')?.enable();

    this.userForm.updateValueAndValidity();
  }

  private setupEditMode(userData: User): void {
    this.userForm.patchValue({
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      username: userData.username || '',
      password: '',
      confirmPassword: '',
    });

    // Password is optional for edit mode
    this.userForm
      .get('password')
      ?.setValidators([
        Validators.minLength(8),
        Validators.pattern(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/),
      ]);
    this.userForm.get('confirmPassword')?.setValidators([]);

    this.userForm.get('email')?.disable();
    this.selectedRole.set({
      value: userData.roles || '',
      label: userData.roles.toLocaleUpperCase() || '',
    });
    this.userForm.updateValueAndValidity();
  }

  // Validation methods
  shouldShowError(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  passwordsDoNotMatch(): boolean {
    const password = this.password?.value;
    const confirmPassword = this.confirmPassword?.value;
    return !!(
      password &&
      confirmPassword &&
      password !== confirmPassword &&
      this.confirmPassword?.touched
    );
  }

  isFormValid(): boolean {
    const formValid = this.userForm.valid;

    if (this.mode() === 'edit') {
      const passwordValue = this.password?.value?.trim();
      const confirmPasswordValue = this.confirmPassword?.value?.trim();

      // If both password fields are empty, that's valid (no password change)
      if (!passwordValue && !confirmPasswordValue) {
        const formValidExcludingPasswords =
          !!this.firstName?.valid &&
          !!this.lastName?.valid &&
          (!!this.email?.valid || !!this.email?.disabled) &&
          !!this.username?.valid;
        return formValidExcludingPasswords;
      }

      // If password is provided, passwords must match and both fields must be valid
      if (passwordValue || confirmPasswordValue) {
        const passwordsMatch = passwordValue === confirmPasswordValue;
        const passwordFieldValid = this.password?.valid;
        return (
          !!formValid &&
          !!passwordsMatch &&
          !!passwordFieldValid &&
          !!passwordValue
        );
      }

      return formValid;
    } else {
      // In create mode, password is required and passwords must match
      const passwordValue = this.password?.value?.trim();
      const confirmPasswordValue = this.confirmPassword?.value?.trim();
      const passwordsMatch = passwordValue === confirmPasswordValue;

      return (
        formValid && passwordsMatch && !!passwordValue && !!confirmPasswordValue
      );
    }
  }

  // Error message methods
  getFirstNameError(): string {
    if (this.firstName?.hasError('required')) return 'First name is required';
    if (this.firstName?.hasError('minlength')) return 'First name is required';
    return '';
  }

  getLastNameError(): string {
    if (this.lastName?.hasError('required')) return 'Last name is required';
    if (this.lastName?.hasError('minlength')) return 'Last name is required';
    return '';
  }

  getEmailError(): string {
    if (this.email?.hasError('required')) return 'Email is required';
    if (this.email?.hasError('email'))
      return 'Please enter a valid email address';
    return '';
  }

  getUsernameError(): string {
    if (this.username?.hasError('required')) return 'Username is required';
    if (this.username?.hasError('pattern'))
      return 'Username must start with a letter and contain only letters, numbers, and underscores';
    if (this.username?.hasError('minlength'))
      return 'Username must be at least 3 characters';
    if (this.username?.hasError('maxlength'))
      return 'Username must be no more than 30 characters';
    return '';
  }

  getPasswordError(): string {
    if (this.password?.hasError('required')) return 'Password is required';
    if (this.password?.hasError('minlength'))
      return 'Password must be at least 8 characters';
    if (this.password?.hasError('pattern'))
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    return '';
  }

  getConfirmPasswordError(): string {
    if (this.confirmPassword?.hasError('required'))
      return 'Please confirm your password';
    if (this.passwordsDoNotMatch()) return 'Passwords do not match';
    return '';
  }

  // UI helper methods
  getTitle(): string {
    return this.mode() === 'create' ? 'Create User' : 'Edit User';
  }

  getSubmitLabel(): string {
    return this.mode() === 'create' ? 'Create' : 'Save';
  }

  onCancel(): void {
    this.resetFormState();
    this.closed.emit();
  }

  async handleSubmit(event: Event) {
    event.preventDefault();

    this.userForm.markAllAsTouched();

    if (!this.isFormValid()) {
      return;
    }

    this.isSubmitting.set(true);

    try {
      const formValue = this.userForm.value;
      let body = {};

      if (this.mode() === 'create') {
        body = {
          username: formValue.username?.trim(),
          password: formValue.password,
          roles: this.selectedRole().value,
          firstName: formValue.firstName?.trim(),
          lastName: formValue.lastName?.trim(),
          email: formValue.email?.trim(),
        };
        await this.apiClient.createUser(body);
        this.showToast('success', 'User created successfully!');
      } else {
        const baseBody = {
          username: formValue.username?.trim(),
          roles: this.selectedRole().value,
          firstName: formValue.firstName?.trim(),
          lastName: formValue.lastName?.trim(),
        };

        if (formValue.password?.trim()) {
          body = { ...baseBody, password: formValue.password };
        } else {
          body = baseBody;
        }

        await this.apiClient.editUser(body, this.userData()!.userId!);
        this.showToast('success', 'User updated successfully!');
      }

      this.resetFormState();
      this.userSaved.emit();
      this.closed.emit();
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse) {
        this.handleApiError(error);
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  // Toast methods
  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  private handleApiError(error: HttpErrorResponse): void {
    console.error(
      `Error ${this.mode() === 'create' ? 'creating' : 'editing'} user:`,
      error
    );

    let errorMessage = `Failed to ${this.mode()} user. Please try again.`;

    if (error?.error?.message) {
      errorMessage = error.error.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }

    this.showToast('error', errorMessage);
  }
}
